// Cities board — the read model.
//
// The board reads a flat card list (the live board's rows, or the baked
// module's cards) and derives the five lanes Jeff asked to see on
// 2026-10-21: who is being worked on right now, what is blocked, what is
// mid-build, what is still queued, and what is finished.
//
// Two rules keep this honest:
//   1. The producer's card contract never changes. The lanes are DERIVED
//      from the card's own fields (state, measuresDone, inProgress) — the
//      ingest side stays exactly as stamped.
//   2. A city that is in flight is never also counted as queued. The lanes
//      partition the board, so the numbers in the summary always add up.
//
// Pure functions only — no React, no data-source knowledge. The board
// component and the tests both read from here.

// The seven parts a city page is made of, in the order they get built.
export const MEASURE_WORD = {
  hero: "Hero image",
  og: "OG image",
  support: "Support scene",
  hospitals: "Hospitals",
  doulas: "Doulas",
  video: "Video",
  outreach: "Outreach",
};

export const MEASURES = Object.keys(MEASURE_WORD).map((key) => ({ key }));

// ── The 4-phase DAG (Jeff directive 2026-10-23, msg 1563212585606713497):
//    build → enrich → verify_deploy → video_outreach, then complete.
//
// The producer stamps `phase` / `phaseIndex` / `phaseLabel` on every card
// (ingest-cities.py derives them from the ledger + the page's own content).
// The model reads them; it never re-guesses them. The constants below are
// the FALLBACK for a card that predates the stamp, so a stale card still
// shows a chip instead of nothing.
export const PHASES = ["build", "enrich", "verify_deploy", "video_outreach"];

export const PHASE_LABEL = {
  build: "Build",
  enrich: "Enrich",
  verify_deploy: "Verify deploy",
  video_outreach: "Video+Outreach",
  complete: "Done",
  blocked: "Blocked",
};

export const PHASE_TONE = {
  build: "amber",
  enrich: "amber",
  verify_deploy: "amber",
  video_outreach: "amber",
  complete: "green",
  blocked: "red",
};

// The chip's tone, derived from the phase when the card carries no tone.
const phaseTone = (phase) => PHASE_TONE[phase] || "amber";

// The phase a card is in. The producer's stamp wins; otherwise the phase is
// derived from the card's own content so an older card still lands somewhere
// honest rather than nowhere.
export const phaseOf = (item) => {
  const p = item?.payload || {};
  const stamped = String(p.phase || "").trim();
  if (stamped && PHASE_LABEL[stamped]) return stamped;
  const lane = laneOf(item);
  if (lane === "blocked") return "blocked";
  if (lane === "done") return "complete";
  const m = p.measures || {};
  if (!m.hero) return "build";
  if (!(m.support && m.hospitals && m.doulas)) return "enrich";
  if (p.built && !m.video) return "verify_deploy";
  if (m.video && !m.outreach) return "video_outreach";
  return "complete";
};

// The phase's position on the ladder: 0 build, 1 enrich, 2 verify deploy,
// 3 video+outreach, 4 done. A blocked city keeps the position of the phase
// it would resume at, so the ladder stays 0-4 for every card.
export const phaseIndexOf = (item) => {
  const p = item?.payload || {};
  if (typeof p.phaseIndex === "number") return p.phaseIndex;
  const phase = phaseOf(item);
  if (phase === "complete") return PHASES.length;
  const i = PHASES.indexOf(phase);
  return i < 0 ? 0 : i;
};

// The word the chip shows.
export const phaseLabelOf = (item) => {
  const p = item?.payload || {};
  if (p.phaseLabel) return String(p.phaseLabel);
  const phase = phaseOf(item);
  return PHASE_LABEL[phase] || PHASE_LABEL.build;
};

// The chip's tone, derived from the phase. A working city in the build
// phase is still amber — the chip says WHERE the page is, not how hard
// Kit is hitting it.
export const phaseToneOf = (item) => phaseTone(phaseOf(item));

// A multi-market provider boost (see ingest-cities.py's priority-boosts.json).
// Zero on every city that was not boosted.
export const boostOf = (item) => {
  const b = item?.payload?.priorityBoost;
  return typeof b === "number" && b > 0 ? b : 0;
};

// Why a city was boosted (null when it was not).
export const boostReasonOf = (item) => {
  const r = item?.payload?.boostReason;
  return r ? String(r) : null;
};

// The lanes of the pipeline, in the order Jeff reads the board in: what is
// moving, what is stuck, what is half-built, what is waiting, what is done.
export const LANE_ORDER = ["working", "blocked", "building", "queued", "done"];

export const LANE_LABEL = {
  working: "Kit is on it — working right now",
  blocked: "Needs attention",
  building: "In build",
  queued: "Queued",
  done: "Completed",
  archived: "Set aside",
};

// Short chip labels — the filter row's vocabulary.
export const LANE_CHIP = {
  working: "Working",
  blocked: "Blocked",
  building: "In build",
  queued: "Queued",
  done: "Completed",
  archived: "Set aside",
};

// "Set aside" is what Jeff's own "Set aside" button does to a city. It is
// not one of the pipeline lanes — it is off the board until he asks for it.
export const ARCHIVED_LANE = "archived";

// A lane the board does not know about (a state the producer invented
// later). Kept visible rather than dropped, so the counts always add up.
export const OTHER_LANE = "other";

export const measuresDone = (item) => {
  const p = item?.payload || {};
  if (typeof p.measuresDone === "number") return p.measuresDone;
  const m = p.measures || {};
  return MEASURES.reduce((n, { key }) => n + (m[key] ? 1 : 0), 0);
};

export const measuresTotal = (item) => {
  const p = item?.payload || {};
  return typeof p.measuresTotal === "number" ? p.measuresTotal : MEASURES.length;
};

// The lane a card belongs to. `working` wins over everything: a city Kit
// is on right now is not also sitting in the queue.
export const laneOf = (item) => {
  const p = item?.payload || {};
  const state = String(item?.state || "").toLowerCase();
  if (state === ARCHIVED_LANE) return ARCHIVED_LANE;
  if (state === "working" || p.inProgress) return "working";
  if (state === "blocked") return "blocked";
  if (state === "done") return "done";
  if (state === "queued") return "queued";
  // "building" in the producer's vocabulary covers both "started, part
  // finished" and "not started yet" — the measure count tells them apart.
  if (state === "building") return measuresDone(item) > 0 ? "building" : "queued";
  return OTHER_LANE;
};

// The state (US state) a city sits in, read off the card's own title
// ("Norfolk, VA") and slug. Used to filter a 1,237-card board down to
// one state without asking Kit for a new board.
const areaOf = (item) => {
  const p = item?.payload || {};
  const fromTitle = String(p.title || "").match(/,\s*([A-Z]{2})\s*$/);
  if (fromTitle) return fromTitle[1];
  const fromSlug = String(item?.slug || "").match(/-([a-z]{2})$/);
  return fromSlug ? fromSlug[1].toUpperCase() : null;
};

const rankOf = (item) => {
  const r = item?.payload?.priorityRank;
  return typeof r === "number" ? r : Number.POSITIVE_INFINITY;
};

// Build priority first (the planner's own order), then the board's own sort.
const byPriority = (a, b) => rankOf(a) - rankOf(b) || (a?.sort ?? 0) - (b?.sort ?? 0);

// Most recently finished first; cities with no completion date fall to the
// bottom rather than pretending to be fresh.
const byCompleted = (a, b) => {
  const ca = a?.payload?.completedAt || "";
  const cb = b?.payload?.completedAt || "";
  if (ca && cb) return cb.localeCompare(ca);
  if (ca) return -1;
  if (cb) return 1;
  return byPriority(a, b);
};

export const buildLanes = (items) => {
  const lanes = {
    working: [],
    blocked: [],
    building: [],
    queued: [],
    done: [],
    archived: [],
    other: [],
  };
  for (const item of items || []) {
    const lane = laneOf(item);
    if (lanes[lane]) lanes[lane].push(item);
    else lanes[OTHER_LANE].push(item);
  }
  lanes.working.sort(byPriority);
  lanes.blocked.sort(byPriority);
  lanes.building.sort(byPriority);
  lanes.queued.sort(byPriority);
  lanes.done.sort(byCompleted);
  lanes.archived.sort(byPriority);
  lanes.other.sort(byPriority);
  return lanes;
};

// The finished board, in summary form: how many, when they landed, and how
// many have no date at all. Jeff asked to SEE where the completed ones are
// — this is that answer without the wall.
export const completedSummary = (doneItems) => {
  const months = new Map();
  let dated = 0;
  let undated = 0;
  for (const item of doneItems || []) {
    const at = item?.payload?.completedAt;
    if (!at) {
      undated += 1;
      continue;
    }
    dated += 1;
    const key = String(at).slice(0, 7);
    months.set(key, (months.get(key) || 0) + 1);
  }
  const byMonth = [...months.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([key, count]) => ({ key, count, label: monthLabel(key) }));
  return {
    total: (doneItems || []).length,
    dated,
    undated,
    byMonth,
    newest: (doneItems || [])[0] || null,
  };
};

const monthLabel = (key) => {
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const m = /^(\d{4})-(\d{2})$/.exec(String(key || ""));
  if (!m) return String(key || "");
  const name = months[Number(m[2]) - 1] || m[2];
  return `${name} ${m[1].slice(2)}`;
};

// "2 in October 26" — how the finished work spread over the last few months.
export const completedBreakdown = (summary) => {
  if (!summary || !summary.byMonth.length) return "";
  const parts = summary.byMonth.map((m) => `${m.count} in ${m.label}`);
  if (summary.undated) parts.push(`${summary.undated} without a date`);
  return parts.join(" · ");
};

// The city that is up for being worked on next: the highest-priority city
// that has not started. A blocked city is not "up" — it waits for Jeff.
export const nextUp = (lanes) => {
  const candidates = [...(lanes.queued || []), ...(lanes.building || [])];
  if (!candidates.length) return null;
  return [...candidates].sort(byPriority)[0] || null;
};

// ── The next-up strip (Jeff directive 2026-10-23).
//
// The producer stamps `meta.nextUp` — the first 5 cities of the NEW board
// order that are not done, not blocked and not being worked on right now.
// The order is: partway-done pages worth finishing first, then new pages by
// value (rank), with a boosted city (a multi-market provider) at the top.
//
// Two sources, one answer:
//   1. the producer's stamp (the live board's truth);
//   2. the board's own cards, re-sorted the same way — used when the stamp
//      is missing (an older baked module) or when the filters hide one of
//      the stamped cities. The board never names a city it is not showing.
//
// `count` is how many candidates to return. The board asks for more than
// it shows (25) so a filter that hides one city still leaves five on
// screen, and slices the result itself.
export const nextUpList = (items, meta, count = 5) => {
  const cards = items || [];
  // The board's own order: the producer's `sort` column already encodes the
  // new order (partway-first, then rank, done last), so a plain sort by
  // `sort` reproduces it exactly.
  const ordered = [...cards].sort((a, b) => (a?.sort ?? 0) - (b?.sort ?? 0) || String(a?.slug).localeCompare(String(b?.slug)));
  const eligible = ordered.filter((item) => {
    const lane = laneOf(item);
    return lane !== "done" && lane !== "blocked" && lane !== "working" && lane !== "archived";
  });

  const stamped = Array.isArray(meta?.nextUp) ? meta.nextUp : [];
  const bySlug = new Map(cards.map((c) => [c.slug, c]));
  const out = [];
  const seen = new Set();
  // A row, from the card. The stamp only overrides what it knows better.
  const row = (card, over = {}) => ({
    slug: card.slug,
    title: card.payload?.title,
    phase: phaseOf(card),
    phaseIndex: phaseIndexOf(card),
    phaseLabel: phaseLabelOf(card),
    priorityRank: card.payload?.priorityRank ?? null,
    priorityBoost: boostOf(card),
    boostReason: boostReasonOf(card),
    measuresDone: measuresDone(card),
    measuresTotal: measuresTotal(card),
    card,
    ...over,
  });

  // The stamped list first — it is the producer's own answer.
  for (const s of stamped) {
    if (!s || !s.slug || seen.has(s.slug)) continue;
    const card = bySlug.get(s.slug);
    if (!card) continue; // the board never names a city it is not showing
    seen.add(s.slug);
    out.push(row(card, {
      title: s.title || card.payload?.title,
      phase: s.phase || phaseOf(card),
      phaseIndex: typeof s.phaseIndex === "number" ? s.phaseIndex : phaseIndexOf(card),
      phaseLabel: s.phaseLabel || phaseLabelOf(card),
      priorityRank: typeof s.priorityRank === "number" ? s.priorityRank : card.payload?.priorityRank ?? null,
      priorityBoost: boostOf(card) || s.priorityBoost || 0,
      boostReason: boostReasonOf(card) || s.boostReason || null,
      measuresDone: typeof s.measuresDone === "number" ? s.measuresDone : measuresDone(card),
      measuresTotal: typeof s.measuresTotal === "number" ? s.measuresTotal : measuresTotal(card),
    }));
    if (out.length >= count) return out;
  }
  // Then the board's own order, for whatever the stamp did not cover.
  for (const card of eligible) {
    if (seen.has(card.slug)) continue;
    seen.add(card.slug);
    out.push(row(card));
    if (out.length >= count) break;
  }
  return out;
};

// Every distinct state on the board, with how many cities sit in each.
export const areaCounts = (items) => {
  const counts = new Map();
  for (const item of items || []) {
    const a = areaOf(item);
    if (!a) continue;
    counts.set(a, (counts.get(a) || 0) + 1);
  }
  return [...counts.entries()]
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
};

// Does this card survive the search box, the state picker and the
// "missing this part" picker? One place, so the visible lanes and the
// "your matches are hidden" hint can never disagree.
export const matchesFilters = (item, { query, area, measure } = {}) => {
  const q = String(query || "").trim().toLowerCase();
  if (q) {
    const p = item?.payload || {};
    const title = String(p.title || "").toLowerCase();
    const slug = String(item?.slug || "").toLowerCase();
    const a = areaOf(item);
    const hit =
      title.includes(q) ||
      slug.includes(q) ||
      (q.length === 2 && a && a.toLowerCase() === q);
    if (!hit) return false;
  }
  if (area && areaOf(item) !== area) return false;
  if (measure) {
    const m = item?.payload?.measures || {};
    if (m[measure]) return false; // "missing this part" — a finished city is never missing one
  }
  return true;
};
